import i18n from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PENDING_TITLE } from "@/lib/labels/inlineReference";
import { IconChevronDown, IconChevronRight, IconCornerDownLeft } from "@tabler/icons-react";
import { testIds } from "@/testIds";
import { PENDING_SLOT } from "../refPicker/refPickerModel";
import { NOT_NUMBERED_YET,
  buildPanel,
  normalizeReport,
  problemText,
  rowLabel,
  type ReferenceReport,
  type ReportProblem,
  type TargetRow,
} from "./referencesPanelModel";

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

/**
 * The References panel — cross-references this document defines and uses.
 * (Olivia, SPEC-references §5–6.)
 *
 * Three sections, in order: **Problems**, only when there are any; **In this
 * document**; **Also · N unused**, collapsed and uncoloured. One footer line
 * dates the numbers, and it is the only place the tool mentions compiling.
 *
 * No library section. The `@` picker unifies citations and cross-references;
 * this panel does not — bibliography management is its own surface, and
 * folding it in here would be two tools in one.
 *
 * It replaces the old "Labels & references" panel, which was bound to the
 * coloured organisational tags rather than to labels at all.
 */
export function ReferencesPanel({
  documentId,
  fetchWithAuth,
  onGoToBlock,
  onReferenceHere,
  refreshKey,
}: {
  documentId: string;
  fetchWithAuth: Fetch;
  /** Scroll the editor to a block. */
  onGoToBlock: (blockId: string) => void;
  /** Insert a reference to this target at the caret — the picker's mirror. */
  onReferenceHere: (target: TargetRow) => void;
  /** Bump to refetch — the document changes under the panel. */
  refreshKey?: unknown;
}) {
  const [report, setReport] = useState<ReferenceReport | null>(null);
  const [failed, setFailed] = useState(false);
  const [showUnused, setShowUnused] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`/api/documents/${documentId}/references`);
      if (!res.ok) throw new Error(String(res.status));
      setReport(normalizeReport(await res.json()));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [documentId, fetchWithAuth]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const panel = useMemo(() => (report ? buildPanel(report) : null), [report]);

  if (failed && !panel) {
    return (
      <div data-testid={testIds.references.panel} className="p-4 text-[12.5px] text-[color:var(--lilia-text-secondary)]">
        Could not load this document&apos;s references.
      </div>
    );
  }
  if (!panel) {
    return <div data-testid={testIds.references.panel} className="p-4" aria-busy="true" />;
  }

  const nothing = panel.targets.length === 0 && panel.unused.length === 0 && panel.problems.length === 0;

  return (
    <div data-testid={testIds.references.panel} className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto pb-2">
        {panel.problems.length > 0 && (
          <Section title={i18n.t("editor:refs.problems")}>
            {panel.problems.map((p) => (
              <ProblemRow key={`${p.kind}:${p.key}`} problem={p} onGoToBlock={onGoToBlock} />
            ))}
          </Section>
        )}

        {panel.targets.length > 0 && (
          <Section title={`In this document · ${panel.targets.length}`}>
            {panel.targets.map((t) => (
              <TargetRowView key={t.key} row={t} onGoToBlock={onGoToBlock} onReferenceHere={onReferenceHere} />
            ))}
          </Section>
        )}

        {panel.unused.length > 0 && (
          <div data-testid={testIds.references.unusedGroup} data-open={showUnused ? "true" : "false"}>
            <button
              type="button"
              data-testid={testIds.references.unusedToggle}
              onClick={() => setShowUnused((v) => !v)}
              aria-expanded={showUnused}
              className="flex w-full items-center gap-1.5 px-3 pb-1 pt-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-[color:var(--lilia-text-secondary)]"
            >
              Also · {panel.unused.length} unused
              {showUnused ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
            </button>
            {showUnused &&
              panel.unused.map((t) => (
                <TargetRowView key={t.key} row={t} onGoToBlock={onGoToBlock} onReferenceHere={onReferenceHere} />
              ))}
          </div>
        )}

        {nothing && (
          <p className="px-4 py-6 text-[12.5px] leading-relaxed text-[color:var(--lilia-text-secondary)]">
            Nothing is labelled yet. A table or figure gets a label from its caption when you save it, and{" "}
            <span className="font-mono">@</span> {i18n.t("editor:refs.inTheTextPointsAt")}
          </p>
        )}
      </div>

      {panel.footer && (
        <div
          data-testid={testIds.references.numberedAt}
          className="px-3 py-2 text-[11px] text-[color:var(--lilia-text-secondary)]"
          style={{ borderTop: "1px solid var(--lilia-border)" }}
        >
          {panel.footer === NOT_NUMBERED_YET ? (
            <>
              <strong className="font-semibold">{i18n.t("editor:refs.notNumberedYet")}</strong>
              {NOT_NUMBERED_YET.slice(i18n.t("editor:refs.notNumberedYet").length)}
            </>
          ) : (
            panel.footer
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="px-3 pb-1 pt-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-[color:var(--lilia-text-secondary)]">
        {title}
      </div>
      {children}
    </div>
  );
}

function ProblemRow({ problem, onGoToBlock }: { problem: ReportProblem; onGoToBlock: (id: string) => void }) {
  // Block-level, not a character offset: the rail says which block and goes
  // there. A location line that pointed vaguely would be worse than none.
  const first = problem.blockIds[0];
  return (
    <button
      type="button"
      data-testid={testIds.references.problem}
      data-problem-kind={problem.kind}
      data-ref-key={problem.key}
      onClick={() => first && onGoToBlock(first)}
      className="block w-full px-3 py-1.5 text-left hover:bg-[var(--lilia-hover)]"
    >
      <span className="block text-[12.5px]" style={{ color: "var(--lilia-warning, #b5830e)" }}>
        {problemText(problem)}
      </span>
      <span className="block text-[11px] text-[color:var(--lilia-text-secondary)]">{i18n.t("editor:refs.goToBlock")}</span>
    </button>
  );
}

function TargetRowView({
  row,
  onGoToBlock,
  onReferenceHere,
}: {
  row: TargetRow;
  onGoToBlock: (id: string) => void;
  onReferenceHere: (t: TargetRow) => void;
}) {
  return (
    <div
      data-testid={testIds.references.target}
      data-ref-key={row.key}
      className="group flex items-center gap-2 px-3 py-1 hover:bg-[var(--lilia-hover)]"
    >
      <button
        type="button"
        onClick={() => onGoToBlock(row.blockId)}
        title={row.key}
        className="flex min-w-0 flex-1 items-baseline gap-2 text-left"
      >
        <span
          className="w-[2.4ch] shrink-0 text-right font-mono text-[12px] tabular-nums"
          style={{ color: row.number ? "var(--lilia-text)" : "var(--lilia-text-secondary)" }}
          title={row.number ? undefined : PENDING_TITLE}
        >
          {row.number ?? PENDING_SLOT}
        </span>
        <span className="min-w-0 flex-1 truncate text-[12.5px] text-[color:var(--lilia-text)]">{rowLabel(row)}</span>
        <span className="shrink-0 text-[11px] tabular-nums text-[color:var(--lilia-text-secondary)]">
          {row.uses > 0 ? `${row.uses}×` : ""}
        </span>
      </button>
      <button
        type="button"
        data-testid={testIds.references.useHere}
        data-ref-key={row.key}
        onMouseDown={(e) => e.preventDefault() /* keep the editor's caret */}
        onClick={() => onReferenceHere(row)}
        title={i18n.t("editor:refs.referenceThisHere")}
        aria-label={`Reference ${row.key} here`}
        className="shrink-0 rounded p-1 text-[color:var(--lilia-text-secondary)] opacity-0 hover:bg-[var(--lilia-bg)] focus:opacity-100 group-hover:opacity-100"
      >
        <IconCornerDownLeft size={14} />
      </button>
    </div>
  );
}
